import { paginate, type RestSchema } from "@mountaya/client";
import { z } from "zod";

import { entitlementSchema, type ProductCapability } from "../../organizations/rules/queries";
import { organizationRoleSchema } from "../../organizations/rules/roles";
import { mountayaFor, type RestClientConfig } from "../../rest/rules/sdk";

/**
 * UserOrganization is one organization the authenticated user belongs to: the slice of the
 * contract's `Membership` the membership checks read, the organization's id, name and the
 * products it holds (`organization_capabilities`) beside the role, and what Mountaya answers the
 * user may use of Map Studio there (`studio_entitlements`). It is picked from the contract's type
 * rather than declared beside it, so a field the contract renames fails to compile here rather
 * than parsing to undefined.
 *
 * Every membership listed is one the user has joined: an invitation waiting for their answer is
 * no membership, and is read on its own ({@link fetchMyInvitations} in ./invitations). How many
 * itineraries the user may keep is no property of a membership either: an organization's
 * itineraries are bounded by its agreement, and the personal space's ceiling is read with the
 * personal space (./personalSpace).
 *
 * The features are the strings the wire carries ({@link studioEntitlementsSchema}): the studio's
 * own vocabulary is a table `@mountaya/studio` publishes, which this leaf does not reach, so the
 * one reader of them, the identity summary, keeps the members that table names.
 */
export type UserOrganization = WithWireFeatures<
  Pick<
    RestSchema.Membership,
    | "organization_capabilities"
    | "organization_id"
    | "organization_name"
    | "role"
    | "studio_entitlements"
  >
>;

/**
 * WithWireFeatures is a scope's answer read with its features as the wire spells them, strings,
 * rather than as the contract's `StudioEntitlement`: the vocabulary that reads them is the table
 * `@mountaya/studio` publishes, and this leaf does not reach it.
 */
export type WithWireFeatures<T extends { readonly studio_entitlements: readonly unknown[] }> = Omit<
  T,
  "studio_entitlements"
> & {
  /** The features of Map Studio the answer lists, as the wire spells them. */
  readonly studio_entitlements: readonly string[];
};

/**
 * studioEntitlementsSchema parses the features a scope's answer lists leniently: the strings it
 * carries, members of the contract's `StudioEntitlement` this console may not all know yet, and
 * none for an answer that carries no list. The studio's vocabulary is read where it is published,
 * so a feature this console does not know is dropped there rather than failing the read here; an
 * answer offering nothing is the safe direction for a list that only ever shows an affordance the
 * server enforces anyway.
 */
export const studioEntitlementsSchema = z
  .array(z.unknown())
  .catch([])
  .transform((values) => values.filter((value): value is string => typeof value === "string"));

/**
 * itineraryLimitSchema parses a scope's itinerary ceiling leniently: a count, or null where no
 * ceiling applies, and null for an answer that carries none or something that is no count. The
 * server refuses a create past its ceiling whatever this reads, so a missing number withholds
 * nothing it would not refuse.
 */
export const itineraryLimitSchema = z.number().int().nonnegative().nullable().catch(null);

/**
 * organizationCapabilitiesSchema parses the products a membership's organization holds
 * leniently: a capability this console does not know yet is dropped rather than failing the
 * whole membership read, and a read that carries none (or something that is no list) reads as
 * holding nothing, the answer that offers no product's surface.
 */
const organizationCapabilitiesSchema = z
  .array(z.unknown())
  .catch([])
  .transform((values) =>
    values.filter(
      (value): value is ProductCapability => entitlementSchema.safeParse(value).success,
    ),
  );

/**
 * userOrganizationSchema parses that slice at the boundary. The role enum is the hoisted
 * definition shared with the organization reads and the membership predicates, and `satisfies`
 * holds the whole object to the contract's type. The capabilities and the studio entitlements
 * parse leniently, so an answer missing them reads as offering nothing rather than failing the
 * read the route guard decides on. Extra wire fields are dropped by the non-strict parse.
 */
const userOrganizationSchema = z.object({
  organization_capabilities: organizationCapabilitiesSchema,
  organization_id: z.string(),
  organization_name: z.string(),
  role: organizationRoleSchema,
  studio_entitlements: studioEntitlementsSchema,
}) satisfies z.ZodType<UserOrganization>;

/**
 * userOrganizationsQueryKey is the prefix of every user's organizations query key.
 * Defined in the hookless layer so both the useQuery hook and render-free callers
 * (the route guard) share one source of truth, and so a write that changes
 * memberships invalidates the list whichever user it was read for.
 */
export const userOrganizationsQueryKey = ["users", "organizations"] as const;

/**
 * fetchUserOrganizations is a hookless query-fn: it carries no React-hook imports so render-free
 * callers (the _app route guard) can call it directly. It reads `getMyOrganizations`, the
 * memberships of the user the console session belongs to: the session names the user, so the
 * request names nobody. The request goes through the published client's generated operation, as
 * every call of a published domain does, so its path is the contract's rather than one written
 * here. The credential comes from the config (the session
 * default sends the Ory ory_session_* cookie, since the SPA cannot read the httpOnly cookie to
 * build a Bearer header). A non-ok response throws the transport's typed error, so TanStack
 * Query drives its error state.
 *
 * The listing pages by cursor, and the guard asks whether the user has any organization at all,
 * so the whole listing is walked: the SDK's `paginate` follows `next_cursor` until it reads null.
 *
 * The Zod parse stays. Boundary validation is this package's own — the transport asserts the
 * response type rather than checking it, which is right for a generated shape and not enough
 * for a narrow slice this module declares itself. The guard that decides whether a signed-in
 * user has any organization at all reads it, so a wire shape that drifted must fail loudly here
 * rather than resolve to an empty list and route somebody to onboarding they already finished.
 */
export const fetchUserOrganizations = async (
  config: RestClientConfig,
): Promise<UserOrganization[]> => {
  const memberships: UserOrganization[] = [];

  for await (const row of paginate(mountayaFor(config).admin.users.getMyOrganizations, {})) {
    memberships.push(userOrganizationSchema.parse(row));
  }

  return memberships;
};

/**
 * userOrganizationsQueryOptions bundles the query key + hookless fetch fn for
 * queryClient.ensureQueryData (the guard) and useQuery (the hook) alike, so both
 * the render-free guard and the React layer share one cache entry per user. The user is in the
 * key although the request does not name them: the cookie answers for whoever is signed in, and
 * the cache must not hand one person's memberships to the next one signed in on the same tab.
 */
export const userOrganizationsQueryOptions = (config: RestClientConfig, userId: string) => ({
  queryFn: () => fetchUserOrganizations(config),
  queryKey: [...userOrganizationsQueryKey, userId] as const,
});
